import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Heart, ShoppingBag, ShoppingCart, Home, Grid, User, Camera } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import api from '../../utils/api';
import { cldImg } from '../../utils/cloudinary';

function AnimatedPlaceholder({ categories }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const words = categories.map(c => c.name);

  useEffect(() => {
    if (words.length === 0) return;
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(i => (i + 1) % words.length);
        setVisible(true);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [words.length]);

  if (words.length === 0) return null;

  return (
    <span className="flex items-center gap-1 text-gray-700 text-sm select-none pointer-events-none">
      Search for&nbsp;
      <span style={{
        display: 'inline-block',
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        transform: visible ? 'translateY(0)' : 'translateY(-8px)',
        opacity: visible ? 1 : 0,
      }}>
        {words[index]}
      </span>
    </span>
  );
}

function SearchBox({ inputRef, categories = [], onSearch, showCamera = true, variant = 'desktop' }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef();
  const boxRef = useRef();

  const fetchSuggestions = useCallback(async (q) => {
    if (q.trim().length < 2) { setSuggestions([]); return; }
    try {
      const { data } = await api.get('/products', { params: { search: q, limit: 6, page: 1 } });
      setSuggestions(data.products || []);
    } catch { setSuggestions([]); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowSugg(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/products?search=${encodeURIComponent(query.trim())}`);
      setQuery(''); setSuggestions([]); setShowSugg(false);
      onSearch?.();
    }
  };

  const handlePick = (slug) => {
    navigate(`/product/${slug}`);
    setQuery(''); setSuggestions([]); setShowSugg(false);
  };

  useEffect(() => {
    const handler = (e) => { if (!boxRef.current?.contains(e.target)) setShowSugg(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={boxRef} className="relative w-full">
      <form onSubmit={handleSubmit} className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
        {!query && !focused && (
          <div className="absolute left-10 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
            {categories.length > 0
              ? <AnimatedPlaceholder categories={categories} />
              : <span className="text-gray-700 text-sm">Search for products...</span>
            }
          </div>
        )}
        <input
          ref={inputRef}
          type="search"
          name="site-search"
          value={query}
          onChange={handleChange}
          onFocus={() => { setFocused(true); query.length >= 2 && setShowSugg(true); }}
          onBlur={() => setFocused(false)}
          placeholder=""
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="search"
          enterKeyHint="search"
          style={{ fontSize: '16px' }}
          className={`w-full pl-10 pr-16 bg-white focus:outline-none transition-all ${variant === 'mobile' ? 'py-3 rounded-xl shadow-sm border-0' : 'py-2.5 rounded-lg border border-gray-500 focus:border-gray-700 text-black'}`}
        />
        <button
          type="submit"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10 px-4 py-1.5 rounded-lg bg-brand-yellow text-brand-dark text-sm font-semibold hover:opacity-90 active:scale-95 transition">
          Go
        </button>
      </form>
      {showSugg && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
          {suggestions.map(p => {
            const img = p.images?.[0]?.image_url;
            const variant = p.variants?.[0];
            const price = parseFloat(variant?.discount_price) > 0 ? parseFloat(variant?.discount_price) : parseFloat(variant?.price) || 0;
            return (
              <button key={p.id} onClick={() => handlePick(p.slug)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-brand-yellow-light transition-colors text-left">
                <div className="w-10 h-10 rounded-lg bg-gray-50 overflow-hidden flex-shrink-0">
                  {img ? <img src={cldImg(img, 100)} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-lg">🛍️</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-brand-dark line-clamp-1">{p.name}</p>
                  <p className="text-xs text-gray-400">{p.category?.name}</p>
                </div>
                {price && <span className="text-sm font-bold text-brand-dark flex-shrink-0">₹{Number(price).toLocaleString('en-IN')}</span>}
              </button>
            );
          })}
          <button
            onClick={() => { navigate(`/products?search=${encodeURIComponent(query)}`); setShowSugg(false); }}
            className="w-full px-4 py-3 text-sm text-brand-yellow font-semibold hover:bg-brand-yellow-light transition-colors border-t border-gray-50">
            See all results for "{query}"
          </button>
        </div>
      )}
    </div>
  );
}

// ── Bottom Nav (mobile only) ───────────────────────────────────
function BottomNav({ mobileSearchRef }) {
  const { pathname } = useLocation();
  const { cartCount } = useCart();
  const { user } = useAuth();

  // Search tab: jump to the search bar at the top of the page and focus it
  // (instead of opening a separate inline search box down here). The focus()
  // call must stay inside this click handler — on iOS the keyboard only opens
  // when focus happens within the user gesture, so no setTimeout.
  const handleSearchClick = () => {
    const input = mobileSearchRef?.current;
    if (input) input.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tabs = [
    { label: 'Home', icon: Home, href: '/' },
    { label: 'Category', icon: Grid, href: '/categories' },
    { label: 'Search', icon: Search, action: handleSearchClick },
    { label: 'Cart', icon: ShoppingCart, href: '/cart', badge: cartCount },
    { label: 'Account', icon: User, href: user ? '/account' : '/login' },
  ];

  const isActive = (href) => {
    if (!href) return false;
    if (href === '/') return pathname === '/';
    if (href === '/categories') return pathname.startsWith('/categories') || pathname.startsWith('/category/');
    return pathname.startsWith(href);
  };

  return (
    <>
      {/* Bottom bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 md:hidden">
        <div className="flex items-center">
          {tabs.map(tab => {
            const active = tab.href ? isActive(tab.href) : false;
            const Icon = tab.icon;
            const inner = (
              <div className={`flex flex-col items-center gap-0.5 py-2 px-1 relative ${active ? 'text-brand-yellow' : 'text-gray-500'}`}>
                <div className="relative">
                  <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} />
                  {tab.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-brand-red text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                      {tab.badge > 9 ? '9+' : tab.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-medium leading-none ${active ? 'font-semibold' : ''}`}>{tab.label}</span>
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-brand-yellow rounded-full" />}
              </div>
            );

            return tab.action ? (
              <button key={tab.label} onClick={tab.action} className="flex-1">
                {inner}
              </button>
            ) : (
              <Link key={tab.label} to={tab.href} className="flex-1">
                {inner}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}

// ── Main Navbar ────────────────────────────────────────────────
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menu, setMenu] = useState([]);
  const { cartCount, logoUrl, logoHeightDesktop, logoHeightMobile } = useCart();
  const { wishlistIds } = useWishlist();
  const { user } = useAuth();
  const wishlistCount = wishlistIds?.size || 0;
  const mobileSearchRef = useRef();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    api.get('/categories')
      .then(r => {
        const list = (r.data.categories || []).filter(
          c => !/cake\s*topper/i.test(c.name || '')
        );
        setCategories(list);
      })
      .catch(() => {});
  }, []);

  // Desktop header menu — admin-controlled (labels, order, show/hide).
  useEffect(() => {
    api.get('/menu')
      .then(r => setMenu(r.data.menu || []))
      .catch(() => {});
  }, []);

  return (
    <>
      <header className={`sticky top-0 z-40 bg-[#ffc845] md:bg-white transition-shadow duration-300 ${scrolled ? 'shadow-md' : ''}`}>

        {/* ── Mobile top bar ── */}
        <div className="md:hidden px-4 pt-3 pb-3" style={{ backgroundColor: '#ffc845' }}>
          {/* Row 1: Logo left, "TheYellove.in" centered, icons right */}
          <div className="relative flex items-center mb-2.5">
            <Link to="/" className="flex-shrink-0">
              <img src={cldImg(logoUrl, 300) || '/logo.png'} alt="TheYellove" style={{ height: `${logoHeightMobile}px` }} className="w-auto object-contain" />
            </Link>
            <Link to="/" className="absolute left-1/2 -translate-x-1/2 font-logo font-bold text-brand-red leading-none whitespace-nowrap text-lg">
              TheYellove
            </Link>
            <div className="flex items-center gap-1 ml-auto">
              <Link to="/wishlist" className="relative p-2" style={{ color: '#3a3a3a' }}>
                <Heart className="w-6 h-6" strokeWidth={2.5} />
                {wishlistCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-brand-red text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {wishlistCount > 9 ? '9+' : wishlistCount}
                  </span>
                )}
              </Link>
              <Link to="/cart" className="relative p-1.5" style={{ color: '#3a3a3a' }}>
                <ShoppingCart className="w-6 h-6" strokeWidth={2.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-brand-red text-white text-[9px] rounded-full flex items-center justify-center font-bold px-0.5">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
          {/* Row 2: Search bar */}
          <SearchBox inputRef={mobileSearchRef} categories={categories} showCamera={false} variant="mobile" />
        </div>

        {/* ── Desktop top bar ── */}
        <div className="hidden md:block border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-6">
            <Link to="/" className="flex-shrink-0">
              <img src={cldImg(logoUrl, 300) || '/logo.png'} alt="TheYellove" style={{ height: `${logoHeightDesktop}px` }} className="w-auto object-contain" />
            </Link>
            <div className="flex-1">
              <SearchBox categories={categories} />
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Link to={user ? '/account' : '/login'} className="p-2 text-gray-700 hover:text-brand-dark transition-colors" title={user ? 'My Account' : 'Login'}>
                <User className="w-5 h-5" strokeWidth={1.8} />
              </Link>
              <Link to="/wishlist" className="relative p-2 text-gray-700 hover:text-brand-dark transition-colors">
                <Heart className="w-5 h-5" strokeWidth={1.8} />
                {wishlistCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-brand-red text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {wishlistCount > 9 ? '9+' : wishlistCount}
                  </span>
                )}
              </Link>
              <Link to="/cart" className="relative p-2 text-gray-700 hover:text-brand-dark transition-colors">
                <ShoppingBag className="w-5 h-5" strokeWidth={1.8} />
                {cartCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-brand-red text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* ── Desktop category nav ── */}
        <div className="hidden lg:block border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6">
            <nav className="flex items-center justify-center gap-0 overflow-x-auto scrollbar-hide">
              <Link to="/products"
                className="flex-shrink-0 px-4 py-3 text-sm font-medium text-gray-700 hover:text-brand-dark border-b-2 border-transparent hover:border-brand-yellow transition-all whitespace-nowrap">
                All
              </Link>
              {menu.map(item => {
                const cls = "flex-shrink-0 px-4 py-3 text-sm font-medium text-gray-700 hover:text-brand-dark border-b-2 border-transparent hover:border-brand-yellow transition-all whitespace-nowrap";
                const external = /^https?:\/\//i.test(item.link);
                return external ? (
                  <a key={item.id} href={item.link} className={cls} target="_blank" rel="noopener noreferrer">{item.label}</a>
                ) : (
                  <Link key={item.id} to={item.link} className={cls}>{item.label}</Link>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Bottom nav — mobile only */}
      <BottomNav mobileSearchRef={mobileSearchRef} />
    </>
  );
}
